import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { API_BASE_URL } from '../../config/apiConfig';
import { applyImageFallback, buildProxiedImageUrl } from '../../config/imageUrlHelper';
import { useUser } from '../../contexts/UserContext';
import CalendarioRanking from './CalendarioRanking';

export async function fetchRankingUsuarios(fecha = null) {
  const res = await axios.get(`${API_BASE_URL}/obtener_ranking_usuarios.php`, fecha ? { params: { fecha } } : undefined);
  if (!res.data?.success) throw new Error('No se pudo cargar el ranking de usuarios');
  return {
    fecha: res.data.fecha,
    hoy: res.data.hoy,
    usuarios: Array.isArray(res.data.usuarios) ? res.data.usuarios : [],
    historial: Array.isArray(res.data.historial) ? res.data.historial : [],
    hayPartidasHoy: Boolean(res.data.hay_partidas_hoy),
  };
}

// Ranking de un día. Al cambiar de día, `usuarios` y `fechaMostrada` conservan el
// día anterior hasta que llega el nuevo, para poder desplazar uno sobre otro.
// `usuarios` es null si todavía no hay datos o si falló la carga.
export function useRankingDiario({ activo = true, recargarClave = 0 } = {}) {
  const [fechaElegida, setFechaElegida] = useState(null); // null = hoy
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(activo);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!activo) return undefined;
    let vigente = true;
    setCargando(true);
    setError(false);
    fetchRankingUsuarios(fechaElegida)
      .then((respuesta) => { if (vigente) setDatos(respuesta); })
      .catch(() => { if (vigente) setError(true); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [activo, fechaElegida, recargarClave]);

  return {
    usuarios: error ? null : (datos?.usuarios ?? null),
    fechaMostrada: datos?.fecha ?? null,
    cargando,
    error,
    hoy: datos?.hoy ?? null,
    historial: datos?.historial ?? [],
    hayPartidasHoy: datos?.hayPartidasHoy ?? false,
    fecha: fechaElegida ?? datos?.hoy ?? null,
    elegirFecha: (fecha) => setFechaElegida(fecha === datos?.hoy ? null : fecha),
  };
}

const TRANSICION_DIA = { duration: 0.28, ease: 'easeOut' };
const VARIANTES_DIA = {
  fuera: ({ direccion, sinMovimiento }) => ({ x: sinMovimiento ? 0 : `${direccion * 100}%`, opacity: 0 }),
  dentro: { x: 0, opacity: 1 },
  sale: ({ direccion, sinMovimiento }) => ({ x: sinMovimiento ? 0 : `${-direccion * 100}%`, opacity: 0 }),
};

// Desplaza el contenido al cambiar `clave` (una fecha): un día posterior entra por
// la derecha y uno anterior por la izquierda. Las fechas ISO se comparan como texto.
export function DeslizamientoDia({ clave, ocupado = false, className = '', children }) {
  const sinMovimiento = useReducedMotion();
  const claveRef = useRef(clave);
  const direccionRef = useRef(1);
  if (clave !== claveRef.current) {
    direccionRef.current = clave > claveRef.current ? 1 : -1;
    claveRef.current = clave;
  }
  const custom = { direccion: direccionRef.current, sinMovimiento };

  return (
    <div
      aria-busy={ocupado}
      className={`relative flex min-h-0 flex-1 flex-col overflow-clip transition-opacity ${ocupado ? 'opacity-60' : ''}`}
    >
      <AnimatePresence initial={false} mode="popLayout" custom={custom}>
        <motion.div
          key={clave}
          data-testid="ranking-dia"
          custom={custom}
          variants={VARIANTES_DIA}
          initial="fuera"
          animate="dentro"
          exit="sale"
          transition={TRANSICION_DIA}
          className={className}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

const formatoDia = (fecha, opciones) => new Date(`${fecha}T12:00:00`).toLocaleDateString('es-ES', opciones);

// Selector de día: flechas entre días con partidas y calendario que los marca con un punto.
export function SelectorDiaRanking({ hoy, fecha, historial, hayPartidasHoy, elegirFecha }) {
  const botonRef = useRef(null);
  const [calendarioAbierto, setCalendarioAbierto] = useState(false);
  const diasConPartidas = useMemo(() => {
    const dias = new Map(historial.map((d) => [d.fecha, d.ganadores]));
    if (hayPartidasHoy && hoy) dias.set(hoy, []);
    return dias;
  }, [historial, hayPartidasHoy, hoy]);

  if (!hoy) return null;

  const dias = [hoy, ...historial.map((d) => d.fecha)];
  const indice = dias.indexOf(fecha);
  const anterior = dias[indice + 1];
  const siguiente = indice > 0 ? dias[indice - 1] : undefined;
  const claseFlecha = 'px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-transparent transition-colors';
  const ganadoresDia = historial.find((d) => d.fecha === fecha)?.ganadores;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button type="button" className={claseFlecha} disabled={!anterior} onClick={() => elegirFecha(anterior)} aria-label="Día anterior">&lsaquo;</button>
        <button
          ref={botonRef}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={calendarioAbierto}
          aria-label="Elegir día del ranking"
          onClick={() => setCalendarioAbierto((abierto) => !abierto)}
          className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <span className="truncate">
            {fecha === hoy
              ? `Hoy, ${formatoDia(hoy, { day: 'numeric', month: 'long' })}`
              : formatoDia(fecha, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
        {calendarioAbierto && (
          <CalendarioRanking
            ancla={botonRef}
            fecha={fecha}
            hoy={hoy}
            dias={diasConPartidas}
            onElegir={(dia) => { setCalendarioAbierto(false); elegirFecha(dia); botonRef.current?.focus(); }}
            onCerrar={() => setCalendarioAbierto(false)}
          />
        )}
        <button type="button" className={claseFlecha} disabled={!siguiente} onClick={() => elegirFecha(siguiente)} aria-label="Día siguiente">&rsaquo;</button>
      </div>
      {ganadoresDia && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {ganadoresDia.length === 1 ? 'Mejor del día' : 'Mejores del día'}:{' '}
          <span className="font-semibold text-gray-800 dark:text-white">{ganadoresDia.join(', ')}</span>
        </p>
      )}
    </div>
  );
}

// Misma regla que el ranking de participantes: empatan quienes tienen los
// mismos aciertos y comodines restantes.
export function conPosiciones(lista) {
  let prevAciertos = null;
  let prevComodines = null;
  let posicion = 0;
  return lista.map((u) => {
    const aciertos = Number(u.preguntas_acertadas) || 0;
    const comodines = Number(u.comodines_restantes) || 0;
    if (aciertos !== prevAciertos || comodines !== prevComodines) {
      posicion += 1;
      prevAciertos = aciertos;
      prevComodines = comodines;
    }
    return { ...u, posicion };
  });
}

const estiloPodio = (posicion, esYo) => {
  if (esYo) return 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600';
  if (posicion === 1) return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700';
  if (posicion === 2) return 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600';
  if (posicion === 3) return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700';
  return 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700';
};

export default function RankingUsuarios({ recargarClave = 0 }) {
  const { user } = useUser();
  const { usuarios, fechaMostrada, cargando, error, hoy, historial, hayPartidasHoy, fecha, elegirFecha } = useRankingDiario({ recargarClave });

  const ranking = useMemo(() => conPosiciones(usuarios || []), [usuarios]);
  const yo = ranking.find((u) => Number(u.id) === Number(user?.id));
  const selector = <SelectorDiaRanking hoy={hoy} fecha={fecha} historial={historial} hayPartidasHoy={hayPartidasHoy} elegirFecha={elegirFecha} />;

  if (cargando && usuarios === null) {
    return (
      <div className="space-y-4">
        {selector}
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Cargando ranking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {selector}
        <p className="py-8 text-center text-sm text-red-600 dark:text-red-400">No se pudo cargar el ranking de usuarios.</p>
      </div>
    );
  }

  if (ranking.length === 0) {
    return (
      <div className="space-y-4">
        {selector}
        <DeslizamientoDia clave={fechaMostrada} ocupado={cargando}>
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Hoy todavía no ha jugado nadie. ¡Sé el primero!</p>
        </DeslizamientoDia>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selector}
      <DeslizamientoDia clave={fechaMostrada} ocupado={cargando} className="space-y-4">
      {yo && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {fechaMostrada === hoy ? 'Vas' : 'Quedaste'} <span className="font-bold text-blue-600 dark:text-blue-400">#{yo.posicion}</span> con{' '}
          <span className="font-semibold">{yo.preguntas_acertadas}</span> {yo.preguntas_acertadas === 1 ? 'acierto' : 'aciertos'}.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        {ranking.map((u) => {
          const esYo = Number(u.id) === Number(user?.id);
          return (
            <div
              key={u.id}
              className={`flex flex-col items-center gap-2 p-2 sm:p-3 rounded-xl border transition-colors ${estiloPodio(u.posicion, esYo)}`}
            >
              <span className="font-bold text-xs text-gray-500 dark:text-gray-400">#{u.posicion}</span>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
                {u.imagen ? (
                  <img
                    src={buildProxiedImageUrl(u.imagen)}
                    alt={u.nombre}
                    className="w-full h-full object-cover"
                    onError={applyImageFallback}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                    </svg>
                  </div>
                )}
              </div>
              <span className="font-semibold text-xs sm:text-sm text-gray-800 dark:text-white truncate max-w-full" title={u.nombre}>
                {u.nombre}{esYo ? ' (tú)' : ''}
              </span>
              <div className="flex items-center justify-center gap-3 text-center">
                <div>
                  <div className="text-xs sm:text-sm font-extrabold text-gray-800 dark:text-white leading-none">{u.preguntas_acertadas}</div>
                  <div className="text-[9px] text-gray-400 leading-none">aciertos</div>
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-bold text-emerald-600 dark:text-emerald-400 leading-none">{u.comodines_restantes}</div>
                  <div className="text-[9px] text-gray-400 leading-none">comodines</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </DeslizamientoDia>
    </div>
  );
}
